import { useState } from "react";

const FIELDS = [
  {
    id: "background",
    label: "Your Professional Background",
    placeholder: "e.g. I'm a nurse with 10 years in the NHS, specialising in mental health...",
    hint: "What's your career, role, or industry experience?",
  },
  {
    id: "expertise",
    label: "What People Come To You For",
    placeholder: "e.g. Friends always ask me for advice on managing money and budgeting...",
    hint: "What do people naturally seek your advice or help on?",
  },
  {
    id: "strength",
    label: "Your Natural Superpower",
    placeholder: "e.g. I'm great at breaking down complex ideas so anyone can understand them...",
    hint: "How do you naturally operate — teaching, connecting, building systems, inspiring?",
  },
  {
    id: "audience",
    label: "Who You Want To Serve",
    placeholder: "e.g. Diaspora professionals in corporate who want to start a side business...",
    hint: "Describe the person you most want to help — the more specific, the better.",
  },
  {
    id: "goal",
    label: "Your Online Income Goal",
    placeholder: "e.g. I want to make £3k/month consulting and eventually launch an online course...",
    hint: "What does financial freedom look like for you online?",
  },
];

const WORKBOOK_URL = "https://shop.beacons.ai/diasporachief/aeb10b10-d5c3-439a-ae60-173ba21db257";

const COLORS = {
  cream: "#F7F2EA",
  creamDeep: "#EFE6D8",
  terracotta: "#C1623D",
  terracottaDeep: "#A14E2E",
  black: "#1A1612",
  charcoal: "#3A332C",
  taupe: "#8A7E6E",
  taupeLight: "#B8AC9A",
  border: "#DCD0BC",
  white: "#FFFFFF",
};

export default function DiasporaSkillsAudit() {
  const [page, setPage] = useState(1);
  const [inputs, setInputs] = useState({ background: "", expertise: "", strength: "", audience: "", goal: "" });
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [skillsAnalysis, setSkillsAnalysis] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Page 1 → Page 2: validate 5 fields
  const validateForm = () => {
    const e = {};
    FIELDS.forEach(f => { if (!inputs[f.id].trim()) e[f.id] = "Required"; });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Page 2 → Page 3: validate email gate, then generate analysis
  const handleEmailSubmit = async () => {
    const e = {};
    if (!firstName.trim()) e.firstName = "Required";
    if (!email.trim() || !email.includes("@")) e.email = "Please enter a valid email";
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setLoading(true);
    setPage(3);
    const summary = FIELDS.map(f => `${f.label}: ${inputs[f.id]}`).join("\n");
    try {
      const res = await fetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 900,
          system: `You are The Diaspora Chief — a bold, culturally-aware business strategist helping African and Caribbean diaspora professionals monetise their skills online. Your tone is direct, warm, powerful, and culturally resonant. You must prove this is personalised by directly referencing specific words or details from what they wrote — never write anything generic enough to apply to any random reader. Address the person by their first name (${firstName}) at least once.`,
          messages: [{ role: "user", content: `Based on ${firstName}'s diaspora skills audit answers:\n${summary}\n\nWrite a COMPLETE PERSONALISED SKILLS ANALYSIS with these exact sections:\n\nYOUR DIASPORA ADVANTAGE\n2-3 sentences on ${firstName}'s unique edge as a diaspora professional. Reference at least one specific detail from their answers so it is unmistakably personal — not generic.\n\nYOUR #1 TRANSFERABLE SKILL\nName it boldly based on what ${firstName} actually wrote and explain why it is valuable in the online economy. 2-3 sentences.\n\nYOUR TOP 3 MONETISATION PATHS\nList exactly 3 paths suited to what ${firstName} specifically wrote. For each: name it, one sentence explanation, ONE concrete example of an actual product, service, or session they could sell right now (not a vague category — name the specific thing), and a realistic GBP income range. Format as a numbered list.\n\nYOUR NEXT MOVE\n1-2 sentences telling ${firstName} the single most important first action they should take this week, tied directly to what they wrote above.\n\nUse plain text, no markdown symbols except numbered lists. Keep each section tight and punchy. Under 420 words total.` }],
        }),
      });
      const data = await res.json();
      setSkillsAnalysis(data.content?.map(c => c.text || "").join("") || "");
    } catch {
      setSkillsAnalysis(`${firstName}, your profile reveals a rare combination of lived experience and professional expertise. The online economy is hungry for exactly what you bring. Your cultural background is not a barrier — it is your most compelling differentiator.`);
    }
    setLoading(false);
  };

  const resetAll = () => {
    setPage(1);
    setInputs({ background: "", expertise: "", strength: "", audience: "", goal: "" });
    setFirstName(""); setEmail("");
    setSkillsAnalysis(""); setErrors({});
  };

  const formatText = (text) => {
    if (!text) return null;
    return text.split("\n").map((line, i) => {
      if (!line.trim()) return <div key={i} style={{ height: "12px" }} />;
      const isHeader = /^(YOUR [A-Z#])/i.test(line.trim());
      if (isHeader) return (
        <div key={i} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "12px", fontWeight: "700", letterSpacing: "3px", color: COLORS.terracotta, marginTop: "24px", marginBottom: "8px", textTransform: "uppercase", borderBottom: `1px solid ${COLORS.border}`, paddingBottom: "6px" }}>
          {line}
        </div>
      );
      const isBullet = /^\d+\./.test(line.trim());
      return (
        <div key={i} style={{ fontSize: "16px", lineHeight: "1.85", color: COLORS.charcoal, marginBottom: "4px", paddingLeft: isBullet ? "12px" : "0", fontFamily: "'DM Sans', sans-serif" }}>
          {line}
        </div>
      );
    });
  };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@400;500;700&display=swap');
    * { box-sizing: border-box; }
    @keyframes fadeUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
    @keyframes spin { to { transform: rotate(360deg); } }
    input::placeholder, textarea::placeholder { color: ${COLORS.taupeLight}; }
    input:focus, textarea:focus { outline: none; border-color: ${COLORS.terracotta} !important; }
    .btn-terra:hover { background: ${COLORS.terracottaDeep} !important; }
    .btn-cream:hover { background: ${COLORS.creamDeep} !important; color: ${COLORS.black} !important; }
    .back-btn:hover { color: ${COLORS.terracotta} !important; }
    .workbook-card:hover { border-color: ${COLORS.terracotta} !important; }
  `;

  const pageWrap = {
    minHeight: "100vh",
    background: COLORS.cream,
    fontFamily: "'DM Sans', sans-serif",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "48px 20px 64px",
  };

  const inner = { maxWidth: "620px", width: "100%", animation: "fadeUp 0.7s ease forwards" };

  const thickDivider = <div style={{ width: "100%", height: "3px", background: COLORS.terracotta, margin: "0 0 28px" }} />;

  const sectionHeader = (label) => (
    <div style={{ background: COLORS.black, padding: "14px 20px", marginBottom: "20px" }}>
      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "12px", fontWeight: "700", letterSpacing: "4px", color: COLORS.cream, textTransform: "uppercase" }}>{label}</span>
    </div>
  );

  const inputBase = (err) => ({
    width: "100%",
    background: COLORS.white,
    border: `1.5px solid ${err ? COLORS.terracotta : COLORS.border}`,
    padding: "14px 16px",
    fontSize: "16px",
    fontFamily: "'DM Sans', sans-serif",
    color: COLORS.black,
    lineHeight: "1.6",
    transition: "border-color 0.2s",
    borderRadius: "0",
  });

  // ── PAGE 1: INTRO ─────────────────────────────────────────
  if (page === 1) return (
    <div style={pageWrap}>
      <style>{css}</style>
      <div style={{ ...inner, textAlign: "center" }}>
        <div style={{ width: "72px", height: "72px", background: COLORS.terracotta, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px" }}>
          <span style={{ fontSize: "28px" }}>🌍</span>
        </div>
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", letterSpacing: "5px", color: COLORS.taupe, marginBottom: "10px", textTransform: "uppercase" }}>
          Diaspora Builders Club
        </div>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontStyle: "normal", fontSize: "clamp(38px, 8vw, 64px)", fontWeight: "400", color: COLORS.black, lineHeight: "1.0", margin: "0 0 4px" }}>
          Diaspora
        </h1>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontStyle: "italic", fontSize: "clamp(38px, 8vw, 64px)", fontWeight: "400", color: COLORS.terracotta, lineHeight: "1.0", margin: "0 0 28px" }}>
          Skills Audit
        </h1>
        <div style={{ width: "100%", height: "3px", background: COLORS.terracotta, marginBottom: "28px" }} />
        <p style={{ fontSize: "20px", color: COLORS.charcoal, lineHeight: "1.85", maxWidth: "460px", margin: "0 auto 10px", fontFamily: "'DM Serif Display', serif", fontStyle: "italic" }}>
          Discover your most powerful transferable skill and the exact online income streams built for your diaspora advantage.
        </p>
        <div style={{ width: "100%", height: "1px", background: COLORS.border, margin: "28px 0" }} />
        <div style={{ display: "flex", justifyContent: "center", gap: "32px", marginBottom: "40px", flexWrap: "wrap" }}>
          {["5 Questions", "AI-Powered", "Free Analysis"].map((t, i) => (
            <div key={i} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "12px", letterSpacing: "3px", color: COLORS.black, textTransform: "uppercase", display: "flex", alignItems: "center", gap: "8px", fontWeight: "700" }}>
              <div style={{ width: "5px", height: "5px", background: COLORS.terracotta, borderRadius: "50%" }} />
              {t}
            </div>
          ))}
        </div>
        <button
          className="btn-terra"
          onClick={() => setPage(2)}
          style={{ background: COLORS.terracotta, color: COLORS.white, border: "none", padding: "18px 56px", fontSize: "13px", letterSpacing: "4px", fontFamily: "'DM Sans', sans-serif", fontWeight: "700", cursor: "pointer", textTransform: "uppercase", transition: "background 0.2s", display: "block", margin: "0 auto" }}
        >
          START MY AUDIT
        </button>
        <div style={{ marginTop: "16px", fontFamily: "'DM Sans', sans-serif", fontSize: "11px", letterSpacing: "2px", color: COLORS.taupe, textTransform: "uppercase" }}>
          Free · 3 Minutes · Built for the Diaspora
        </div>
        <div style={{ width: "100%", height: "3px", background: COLORS.black, marginTop: "48px" }} />
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", letterSpacing: "2px", color: COLORS.taupe, marginTop: "14px", textTransform: "uppercase" }}>
          The Diaspora Chief · diasporabuildersclub.com
        </div>
      </div>
    </div>
  );

  // ── PAGE 2: FORM + EMAIL GATE ─────────────────────────────
  if (page === 2) return (
    <div style={pageWrap}>
      <style>{css}</style>
      <div style={inner}>
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div style={{ width: "48px", height: "48px", background: COLORS.terracotta, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <span style={{ fontSize: "20px" }}>🌍</span>
          </div>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", letterSpacing: "5px", color: COLORS.taupe, marginBottom: "8px", textTransform: "uppercase" }}>Your Audit</div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(28px, 5vw, 44px)", fontWeight: "400", color: COLORS.black, margin: "0 0 4px" }}>Tell Us About You</h2>
          {thickDivider}
          <p style={{ fontSize: "17px", color: COLORS.taupe, fontStyle: "italic", margin: 0, lineHeight: "1.7", fontFamily: "'DM Serif Display', serif" }}>The more specific you are, the more powerful your results</p>
        </div>

        {/* 5 QUESTIONS */}
        <div style={{ display: "flex", flexDirection: "column", gap: "28px", marginBottom: "40px" }}>
          {FIELDS.map((field, idx) => (
            <div key={field.id}>
              {sectionHeader(`${String(idx + 1).padStart(2, "0")} · ${field.label}`)}
              <div style={{ fontSize: "15px", color: COLORS.taupe, fontStyle: "italic", marginBottom: "10px", lineHeight: "1.6", fontFamily: "'DM Serif Display', serif" }}>{field.hint}</div>
              <textarea
                rows={3}
                value={inputs[field.id]}
                onChange={e => { setInputs(p => ({ ...p, [field.id]: e.target.value })); if (errors[field.id]) setErrors(p => ({ ...p, [field.id]: null })); }}
                placeholder={field.placeholder}
                style={{ ...inputBase(errors[field.id]), resize: "vertical" }}
              />
              {errors[field.id] && <div style={{ fontSize: "13px", color: COLORS.terracotta, marginTop: "4px", fontFamily: "'DM Sans', sans-serif" }}>{errors[field.id]}</div>}
            </div>
          ))}
        </div>

        {/* EMAIL GATE — sits at the bottom of the form */}
        <div style={{ border: `2px solid ${COLORS.terracotta}`, padding: "32px", background: COLORS.white, marginBottom: "20px" }}>
          {sectionHeader("Where shall we send your analysis?")}
          <p style={{ fontSize: "16px", color: COLORS.charcoal, lineHeight: "1.8", marginBottom: "24px", fontFamily: "'DM Serif Display', serif", fontStyle: "italic" }}>
            Enter your name and email and we'll display your personalised skills analysis on-screen instantly — and send a copy straight to your inbox.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>
            <div>
              <input
                type="text"
                placeholder="Your first name"
                value={firstName}
                onChange={e => { setFirstName(e.target.value); if (errors.firstName) setErrors(p => ({ ...p, firstName: null })); }}
                style={inputBase(errors.firstName)}
              />
              {errors.firstName && <div style={{ fontSize: "13px", color: COLORS.terracotta, marginTop: "4px" }}>Required</div>}
            </div>
            <div>
              <input
                type="email"
                placeholder="Your best email address"
                value={email}
                onChange={e => { setEmail(e.target.value); if (errors.email) setErrors(p => ({ ...p, email: null })); }}
                style={inputBase(errors.email)}
              />
              {errors.email && <div style={{ fontSize: "13px", color: COLORS.terracotta, marginTop: "4px" }}>{errors.email}</div>}
            </div>
          </div>
          <button
            className="btn-terra"
            onClick={() => { if (validateForm()) handleEmailSubmit(); }}
            style={{ background: COLORS.terracotta, color: COLORS.white, border: "none", padding: "18px", width: "100%", fontSize: "13px", letterSpacing: "4px", fontFamily: "'DM Sans', sans-serif", fontWeight: "700", cursor: "pointer", textTransform: "uppercase", transition: "background 0.2s" }}
          >
            ANALYSE MY SKILLS →
          </button>
          <div style={{ textAlign: "center", marginTop: "10px", fontSize: "13px", color: COLORS.taupeLight, fontStyle: "italic", fontFamily: "'DM Serif Display', serif" }}>
            No spam. No fluff. Just your results.
          </div>
        </div>

        <div style={{ textAlign: "center" }}>
          <button className="back-btn" onClick={() => setPage(1)} style={{ background: "none", border: "none", fontSize: "13px", color: COLORS.taupeLight, cursor: "pointer", letterSpacing: "2px", fontFamily: "'DM Sans', sans-serif", textTransform: "uppercase", transition: "color 0.2s" }}>
            ← Back
          </button>
        </div>
      </div>
    </div>
  );

  // ── PAGE 3: RESULTS ──────────────────────────────────────
  return (
    <div style={pageWrap}>
      <style>{css}</style>
      <div style={inner}>

        {/* HEADER */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div style={{ width: "48px", height: "48px", background: COLORS.terracotta, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <span style={{ fontSize: "20px" }}>🌍</span>
          </div>
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", letterSpacing: "5px", color: COLORS.taupe, marginBottom: "8px", textTransform: "uppercase" }}>
            {firstName ? `${firstName}'s Results` : "Your Results"}
          </div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(28px, 5vw, 44px)", fontWeight: "400", color: COLORS.black, margin: 0 }}>Your Skills Analysis</h2>
        </div>
        {thickDivider}

        {/* ANALYSIS CARD */}
        <div style={{ background: COLORS.white, border: `1.5px solid ${COLORS.black}`, padding: "32px", marginBottom: "40px", minHeight: "200px" }}>
          {loading
            ? <div style={{ display: "flex", alignItems: "center", gap: "16px", padding: "24px 0" }}>
                <div style={{ width: "24px", height: "24px", border: `2px solid ${COLORS.border}`, borderTop: `2px solid ${COLORS.terracotta}`, borderRadius: "50%", animation: "spin 0.9s linear infinite", flexShrink: 0 }} />
                <span style={{ fontSize: "16px", color: COLORS.taupe, fontStyle: "italic", fontFamily: "'DM Serif Display', serif" }}>
                  Building your personalised analysis, {firstName}...
                </span>
              </div>
            : <div>{formatText(skillsAnalysis)}</div>
          }
        </div>

        {/* PAID OFFER — £17 WORKBOOK */}
        {!loading && (
          <div style={{ animation: "fadeUp 0.6s ease forwards" }}>

            {/* Divider with label */}
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "32px" }}>
              <div style={{ flex: 1, height: "1px", background: COLORS.border }} />
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", letterSpacing: "3px", color: COLORS.taupe, textTransform: "uppercase", whiteSpace: "nowrap" }}>Don't Build This Alone</div>
              <div style={{ flex: 1, height: "1px", background: COLORS.border }} />
            </div>

            {/* Workbook offer card */}
            <div style={{ background: COLORS.black, padding: "40px 32px", marginBottom: "28px" }}>

              {/* Badge */}
              <div style={{ display: "inline-block", background: COLORS.terracotta, padding: "6px 16px", marginBottom: "20px" }}>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", fontWeight: "700", letterSpacing: "3px", color: COLORS.white, textTransform: "uppercase" }}>£17 · One-Time</span>
              </div>

              <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(24px, 4vw, 36px)", fontWeight: "400", color: COLORS.cream, margin: "0 0 8px", lineHeight: "1.2" }}>
                Your 90-Day Skill to Income
              </h3>
              <h3 style={{ fontFamily: "'DM Serif Display', serif", fontStyle: "italic", fontSize: "clamp(24px, 4vw, 36px)", fontWeight: "400", color: COLORS.terracotta, margin: "0 0 20px", lineHeight: "1.2" }}>
                Game Plan Workbook
              </h3>

              <div style={{ width: "40px", height: "2px", background: COLORS.terracotta, marginBottom: "24px" }} />

              <p style={{ fontSize: "17px", color: COLORS.taupeLight, lineHeight: "1.9", marginBottom: "28px", fontFamily: "'DM Serif Display', serif", fontStyle: "italic" }}>
                Your analysis has shown you the opportunity. The workbook gives you the exact 90-day execution plan, the income model templates, and the step-by-step framework to turn what you know into what you earn — built specifically for diaspora professionals like you.
              </p>

              {/* What you get */}
              <div style={{ background: "#2A2420", padding: "24px", marginBottom: "28px" }}>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", fontWeight: "700", letterSpacing: "3px", color: COLORS.terracotta, textTransform: "uppercase", marginBottom: "16px" }}>What's Inside</div>
                {[
                  "Your personalised 90-Day income launch plan",
                  "Income model templates for consulting, digital products & services",
                  "The Diaspora Advantage framework — how to position your cultural edge",
                  "Week-by-week action checklist so you always know what to do next",
                  "Pricing guides built for the diaspora market",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: "12px", alignItems: "flex-start", marginBottom: "12px" }}>
                    <div style={{ width: "6px", height: "6px", background: COLORS.terracotta, borderRadius: "50%", marginTop: "8px", flexShrink: 0 }} />
                    <div style={{ fontSize: "15px", color: COLORS.taupeLight, lineHeight: "1.7", fontFamily: "'DM Sans', sans-serif" }}>{item}</div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <a
                href={WORKBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-terra"
                style={{ background: COLORS.terracotta, color: COLORS.white, padding: "20px 32px", fontSize: "13px", letterSpacing: "4px", fontFamily: "'DM Sans', sans-serif", fontWeight: "700", textDecoration: "none", display: "block", textAlign: "center", textTransform: "uppercase", transition: "background 0.2s", marginBottom: "12px" }}
              >
                GET THE WORKBOOK — £17 →
              </a>

              <div style={{ textAlign: "center", fontSize: "13px", color: COLORS.taupe, fontStyle: "italic", fontFamily: "'DM Serif Display', serif" }}>
                One-time payment · Instant access · No subscription
              </div>
            </div>

            {/* Footer strip */}
            <div style={{ width: "100%", height: "3px", background: COLORS.terracotta, marginBottom: "14px" }} />
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", letterSpacing: "2px", color: COLORS.taupe, textTransform: "uppercase", textAlign: "center", marginBottom: "24px" }}>
              The Diaspora Chief · diasporabuildersclub.com
            </div>
          </div>
        )}

        <div style={{ textAlign: "center" }}>
          <button className="back-btn" onClick={resetAll} style={{ background: "none", border: "none", fontSize: "13px", color: COLORS.taupeLight, cursor: "pointer", letterSpacing: "2px", fontFamily: "'DM Sans', sans-serif", textTransform: "uppercase", transition: "color 0.2s" }}>
            ↩ Start Again
          </button>
        </div>
      </div>
    </div>
  );
}
